import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Collapse,
  Box,
  Typography,
  CircularProgress,
  Avatar,
  IconButton,
  Link as MuiLink,
  Stack,
  TextField,
  Button,
  Chip,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import type { OrderDTO } from '../../dto/order/OrderDTO';
import { getOrderHistory } from '../../services/orders';
import '../../styles/UserOrderPage.css';

const fmtCurrency = (n: number | undefined | null) =>
  typeof n === 'number' ? `$${n.toFixed(2)}` : '-';

const toDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

const daysAgo = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateInputValue(date);
};

const parseOrderDate = (value?: string) => {
  if (!value) return 0;
  return Date.parse(value.replace(' ', 'T')) || 0;
};

const isInDateRange = (value: string | undefined, from: string, to: string) => {
  const timestamp = parseOrderDate(value);
  if (!timestamp) return false;

  const fromTime = from ? new Date(`${from}T00:00:00`).getTime() : 0;
  const toTime = to ? new Date(`${to}T23:59:59`).getTime() : Infinity;

  return timestamp >= fromTime && timestamp <= toTime;
};

const formatShippingAddress = (order: OrderDTO) => {
  const address = order.shippingAddress;
  if (!address) return 'Shipping details unavailable';

  const street = [address.street1, address.street2].filter(Boolean).join(', ');
  const cityLine = [address.city, address.state, address.zip]
    .filter(Boolean)
    .join(', ');
  const country = address.country?.trim();

  return [street, cityLine, country].filter(Boolean).join(' • ');
};

const UserOrderPage: React.FC = () => {
  const [orders, setOrders] = useState<OrderDTO[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [dateFrom, setDateFrom] = useState(() => daysAgo(90));
  const [dateTo, setDateTo] = useState(() => toDateInputValue(new Date()));

  // helpers
  const ts = parseOrderDate;
  const toggleRow = (id: string) =>
    setOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  const isRowOpen = (id: string) => !!open[id];
  const navigate = useNavigate();

  // newest first
  const filteredOrders = useMemo(
    () =>
      orders
        ? orders.filter((order) => isInDateRange(order.createdAt, dateFrom, dateTo))
        : [],
    [dateFrom, dateTo, orders]
  );

  const sortedOrders = useMemo(
    () => [...filteredOrders].sort((a, b) => ts(b.createdAt) - ts(a.createdAt)),
    [filteredOrders]
  );

  // Require auth if used as a standalone page (e.g., /orders)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    setLoading(true);
    getOrderHistory()
      .then((history) => setOrders(history))
      .catch((err) => {
        console.error('[UserOrderPage] Failed to load order history:', err);
        setOrders([]); // fall back to empty to keep UI responsive
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !orders) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="user-order-page-container">
      <Box className="orders-page-hero">
        <Box>
          <Typography className="orders-eyebrow">Account history</Typography>
          <Typography variant="h4" className="orders-title">
            My Orders
          </Typography>
          <Typography className="orders-subtitle">
            Showing {sortedOrders.length} of {orders.length} orders.
          </Typography>
        </Box>
        <Chip
          className="orders-count-chip"
          label={`${orders.length} total`}
          variant="outlined"
        />
      </Box>

      <Paper className="orders-filter-panel" variant="outlined">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="outlined"
            onClick={() => {
              setDateFrom(daysAgo(90));
              setDateTo(toDateInputValue(new Date()));
            }}
          >
            Last 90 days
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setDateFrom('');
              setDateTo('');
            }}
          >
            Show all
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper} className="orders-table-container">
        <Table className="orders-table">
          <TableHead>
            <TableRow>
              <TableCell width={56} />
              <TableCell align="center">Order ID</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Placed</TableCell>
              <TableCell align="center">Total</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" className="orders-empty-state">
                  No orders match this date range.
                </TableCell>
              </TableRow>
            )}
            {sortedOrders.map((o) => {
              const rowOpen = isRowOpen(o.id);

              return (
                <React.Fragment key={o.id}>
                  {/* SUMMARY ROW */}
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => toggleRow(o.id)}
                    data-open={rowOpen}
                  >
                    <TableCell
                      className="toggle-cell"
                      align="center"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRow(o.id);
                      }}
                    >
                      <IconButton size="small">
                        {rowOpen ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>

                    <TableCell align="center" data-label="Order ID">
                      {o.id}
                    </TableCell>
                    <TableCell align="center" data-label="Status">
                      {o.status}
                    </TableCell>
                    <TableCell align="center" data-label="Placed">
                      {o.createdAt
                        ? new Date(o.createdAt).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell align="center" data-label="Total">
                      {fmtCurrency(o.totalAmount)}
                    </TableCell>
                  </TableRow>

                  {/* DETAILS ROW */}
                  <TableRow className="details-row">
                    <TableCell colSpan={5} sx={{ p: 0 }}>
                      <Collapse in={rowOpen} timeout="auto" unmountOnExit>
                        <Box m={2}>
                          <Typography variant="h6" gutterBottom>
                            Shipping
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {formatShippingAddress(o)}
                          </Typography>

                          <Typography variant="h6" gutterBottom>
                            Items
                          </Typography>

                          <Table size="small" className="order-items-table">
                            <TableHead>
                              <TableRow>
                                <TableCell align="center">Product</TableCell>
                                <TableCell align="center">Size</TableCell>
                                <TableCell align="center">Color</TableCell>
                                <TableCell align="center">Qty</TableCell>
                                <TableCell align="center">Price</TableCell>
                                <TableCell align="center">Line Total</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Tracking</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {(o.items ?? []).map((item) => {
                                const lineTotal =
                                  (item?.itemPrice ?? 0) *
                                  (item?.quantity ?? 0);

                                return (
                                  <TableRow key={item.id}>
                                    <TableCell
                                      align="center"
                                      data-label="Product"
                                      className="product-cell"
                                    >
                                      <Stack
                                        className="product-stack"
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        justifyContent="center"
                                      >
                                        <Avatar
                                          variant="square"
                                          src={item.thumbnail || undefined}
                                          alt={item.itemName}
                                          sx={{ width: 56, height: 56 }}
                                        />
                                        <MuiLink
                                          className="product-name"
                                          component={RouterLink}
                                          to={`/product/${item.itemId}`}
                                          underline="hover"
                                        >
                                          {item.itemName}
                                        </MuiLink>
                                      </Stack>
                                    </TableCell>

                                    <TableCell align="center" data-label="Size">
                                      {item.size ?? '-'}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      data-label="Color"
                                    >
                                      {item.color ?? '-'}
                                    </TableCell>
                                    <TableCell align="center" data-label="Qty">
                                      {item.quantity}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      data-label="Price"
                                    >
                                      {fmtCurrency(item.itemPrice)}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      data-label="Line Total"
                                    >
                                      {fmtCurrency(lineTotal)}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      data-label="Status"
                                    >
                                      {item.status}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      data-label="Tracking"
                                    >
                                      {item.trackingLink ? (
                                        <MuiLink
                                          href={item.trackingLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          underline="hover"
                                        >
                                          Track package
                                        </MuiLink>
                                      ) : (
                                        '-'
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              {/* Order summary row */}
                              <TableRow className="order-total-row">
                                <TableCell colSpan={4} />
                                <TableCell
                                  align="right"
                                  className="order-total-label"
                                >
                                  Order Total:
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="order-total-value"
                                >
                                  {fmtCurrency(o.totalAmount)}
                                </TableCell>
                                <TableCell />
                                <TableCell />
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UserOrderPage;
